import React, { useState, useCallback } from 'react';
import type { CapacitySchedule, CapacitySpan, PoolMode } from '@interlucent/organization-protocol';
import { FormField } from '../shared';
import { ScheduleSpanModal } from './ScheduleSpanModal';
import styles from './CapacityScheduleEditor.module.css';

export interface CapacityScheduleEditorProps {
  /**
   * Current capacity schedule value
   */
  value: CapacitySchedule | undefined;

  /**
   * Callback when schedule changes
   */
  onChange: (schedule: CapacitySchedule) => void;

  /**
   * Whether mediated pool mode is allowed
   */
  allowMediated?: boolean;

  /**
   * Disable all interactions
   * @default false
   */
  disabled?: boolean;

  /**
   * Additional CSS class
   */
  className?: string;
}

/** Detect the user's IANA timezone via the browser */
export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** Format an IANA timezone for display, e.g. "America/New_York" → "America/New York (EDT, UTC-4)" */
function formatTimezone(tz: string): string {
  try {
    const now = new Date();
    const short = now.toLocaleString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop() ?? '';
    const offset = now.toLocaleString('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).split(' ').pop() ?? '';
    const readable = tz.replace(/_/g, ' ');
    return `${readable} (${short}, ${offset})`;
  } catch {
    return tz;
  }
}

/** Build timezone options: user locale first, then UTC, then common zones (deduped) */
function buildTimezoneOptions(): Array<{ value: string; label: string }> {
  const common = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Kolkata',
    'Australia/Sydney',
  ];

  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];

  // User locale first
  if (USER_TIMEZONE && USER_TIMEZONE !== 'UTC') {
    seen.add(USER_TIMEZONE);
    options.push({ value: USER_TIMEZONE, label: `${formatTimezone(USER_TIMEZONE)} — Your locale` });
  }

  // UTC
  seen.add('UTC');
  options.push({ value: 'UTC', label: 'UTC' });

  // Common zones (skip duplicates)
  for (const tz of common) {
    if (!seen.has(tz)) {
      seen.add(tz);
      options.push({ value: tz, label: formatTimezone(tz) });
    }
  }

  return options;
}

export const TIMEZONES = buildTimezoneOptions();

const DEFAULT_SCHEDULE: CapacitySchedule = {
  poolMode: 'standard',
  spans: [],
};

/**
 * CapacityScheduleEditor Component
 *
 * Main container for editing Private Pool capacity schedules.
 * Composes WeeklyGridView, ScheduleSpanModal, and CapacitySlider.
 *
 * Features:
 * - Pool Mode toggle (Standard / Mediated)
 * - Timezone selector
 * - Interactive weekly grid visualization
 * - List of configured spans
 * - Add/Edit/Delete span operations
 * - Bauhaus design system compliance
 *
 * @example
 * ```tsx
 * <CapacityScheduleEditor
 *   value={profile.capacitySchedule}
 *   onChange={(schedule) => updateProfile({ capacitySchedule: schedule })}
 *   allowMediated={true}
 *   disabled={isSaving}
 * />
 * ```
 */
export const CapacityScheduleEditor: React.FC<CapacityScheduleEditorProps> = ({
  value,
  onChange,
  allowMediated = false,
  disabled = false,
  className,
}) => {
  const schedule = value || DEFAULT_SCHEDULE;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSpan, setEditingSpan] = useState<CapacitySpan | undefined>();
  const [initialModalValues, setInitialModalValues] = useState<{
    dayOfWeek?: number;
    startHour?: number;
    endHour?: number;
  }>();

  const handlePoolModeChange = useCallback(
    (mode: PoolMode) => {
      if (disabled) return;
      onChange({
        ...schedule,
        poolMode: mode,
      });
    },
    [disabled, schedule, onChange]
  );

  const handleAddSpanClick = useCallback(() => {
    setEditingSpan(undefined);
    setInitialModalValues(undefined);
    setIsModalOpen(true);
  }, []);

  const handleSpanClick = useCallback((span: CapacitySpan) => {
    setEditingSpan(span);
    setInitialModalValues(undefined);
    setIsModalOpen(true);
  }, []);

  const handleSpanSave = useCallback(
    (spanData: Omit<CapacitySpan, 'id'>) => {
      if (disabled) return;

      if (editingSpan) {
        // Update existing span
        const updatedSpans = schedule.spans.map((s) =>
          s.id === editingSpan.id ? { ...spanData, id: editingSpan.id } : s
        );
        onChange({
          ...schedule,
          spans: updatedSpans,
        });
      } else {
        // Add new span
        const newSpan: CapacitySpan = {
          ...spanData,
          id: crypto.randomUUID(),
        };
        onChange({
          ...schedule,
          spans: [...schedule.spans, newSpan],
        });
      }

      setIsModalOpen(false);
      setEditingSpan(undefined);
      setInitialModalValues(undefined);
    },
    [disabled, editingSpan, schedule, onChange]
  );

  const handleSpanDelete = useCallback(
    (spanId: string) => {
      if (disabled) return;

      const updatedSpans = schedule.spans.filter((s) => s.id !== spanId);
      onChange({
        ...schedule,
        spans: updatedSpans,
      });
    },
    [disabled, schedule, onChange]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingSpan(undefined);
    setInitialModalValues(undefined);
  }, []);

  // Parse a one-time span's startDate/endDate (which may be naive local in `timezone`,
  // or carry an offset) into a Date for display + history comparison.
  const parseSpanDate = useCallback((value: string | undefined, tz: string): Date | null => {
    if (!value) return null;
    // Already zoned — Date.parse handles it.
    if (/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    // Naive — treat as wall-clock in `tz`. We render in that TZ anyway via
    // toLocaleString, so the precise UTC offset only matters for the
    // past/future comparison. Use the same single-pass offset correction the
    // backend uses.
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const [, y, mo, dd, hh, mm, ss] = m;
    const wallUtc = Date.UTC(+y, +mo - 1, +dd, +hh, +mm, ss ? +ss : 0);
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).formatToParts(new Date(wallUtc));
      const lookup: Record<string, string> = {};
      for (const p of parts) lookup[p.type] = p.value;
      const h = lookup.hour === '24' ? '00' : lookup.hour;
      const asUtc = Date.UTC(
        +lookup.year, +lookup.month - 1, +lookup.day,
        +h, +lookup.minute, +lookup.second,
      );
      const offsetMin = (asUtc - wallUtc) / 60000;
      return new Date(wallUtc - offsetMin * 60000);
    } catch {
      return new Date(wallUtc);
    }
  }, []);

  const isSpanPast = useCallback((span: CapacitySpan): boolean => {
    if (span.type !== 'one_time') return false;
    const end = parseSpanDate(span.endDate, span.timezone || 'UTC');
    return end !== null && end.getTime() <= Date.now();
  }, [parseSpanDate]);

  // Format span for display
  const formatSpanSummary = useCallback((span: CapacitySpan): string => {
    if (span.type === 'recurring' && span.daysOfWeek && span.startTime && span.endTime) {
      const days = span.daysOfWeek
        .map((d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
        .join(', ');
      const tz = span.timezone ? ` (${span.timezone.replace(/_/g, ' ')})` : '';
      return `${days} ${span.startTime}-${span.endTime}${tz}`;
    }
    if (span.type === 'one_time' && span.startDate && span.endDate) {
      const tz = span.timezone || 'UTC';
      const start = parseSpanDate(span.startDate, tz);
      const end = parseSpanDate(span.endDate, tz);
      if (!start || !end) return 'One-time schedule';
      const fmt: Intl.DateTimeFormatOptions = {
        timeZone: tz, month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      };
      const startStr = start.toLocaleString('en-US', fmt);
      const endStr = end.toLocaleString('en-US', fmt);
      return `${startStr} – ${endStr} (${tz.replace(/_/g, ' ')})`;
    }
    return 'One-time schedule';
  }, [parseSpanDate]);

  return (
    <div className={`${styles.editor} ${className || ''}`}>
      {/* Pool Mode Toggle */}
      <FormField
        label="Pool Mode"
        htmlFor="pool-mode"
        helperText="Standard: direct connections. Mediated: pool proxies jobs from marketplace."
      >
        <div className={styles.poolModeToggle}>
          <button
            type="button"
            className={`${styles.poolModeButton} ${schedule.poolMode === 'standard' ? styles.poolModeButtonActive : ''}`}
            onClick={() => handlePoolModeChange('standard')}
            disabled={disabled}
          >
            Standard
          </button>
          <button
            type="button"
            className={`${styles.poolModeButton} ${schedule.poolMode === 'mediated' ? styles.poolModeButtonActive : ''}`}
            onClick={() => handlePoolModeChange('mediated')}
            disabled={disabled || !allowMediated}
            title={!allowMediated ? 'Mediated mode not available' : undefined}
          >
            Mediated
          </button>
        </div>
      </FormField>

      {/* Spans List */}
      <div className={styles.spansSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Session Capacity Spans</h3>
          <button
            type="button"
            className={styles.addButton}
            onClick={handleAddSpanClick}
            disabled={disabled}
          >
            + Add Schedule Span
          </button>
        </div>

        {schedule.spans.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No session capacity spans configured</p>
            <p className={styles.emptyStateHint}>
              Define how many concurrent sessions you expect during each time window. The system provisions the right number of machines automatically.
            </p>
          </div>
        ) : (
          <div className={styles.spansList}>
            {[...schedule.spans]
              // Sort past one-time spans to the bottom; otherwise preserve order.
              .sort((a, b) => Number(isSpanPast(a)) - Number(isSpanPast(b)))
              .map((span) => {
              const past = isSpanPast(span);
              return (
                <div
                  key={span.id}
                  className={styles.spanCard}
                  style={past ? { opacity: 0.6 } : undefined}
                >
                  <div className={styles.spanCardContent}>
                    <div className={styles.spanCardTitle}>
                      {span.description || 'Capacity Span'}
                      {past && (
                        <span
                          style={{
                            marginLeft: 'var(--space-2)',
                            padding: '2px 6px',
                            borderRadius: '2px',
                            fontSize: 'var(--text-xs)',
                            background: 'var(--surface-muted, rgba(0,0,0,0.08))',
                            color: 'var(--text-muted, inherit)',
                          }}
                        >
                          Past
                        </span>
                      )}
                    </div>
                    <div className={styles.spanCardDetails}>
                      <div className={styles.spanCardDetail}>
                        <strong>Schedule:</strong> {formatSpanSummary(span)}
                      </div>
                      <div className={styles.spanCardDetail}>
                        {span.floor !== undefined && (
                          <span><strong>Floor:</strong> {span.floor} </span>
                        )}
                        {span.ceiling !== undefined && (
                          <span><strong>Ceiling:</strong> {span.ceiling} </span>
                        )}
                        {span.floor === undefined && span.ceiling === undefined && span.expectedSessions !== undefined && (
                          <span><strong>Sessions:</strong> {span.expectedSessions}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.spanCardActions}>
                    <button
                      type="button"
                      className={styles.spanActionButton}
                      onClick={() => handleSpanClick(span)}
                      disabled={disabled}
                      title={past ? 'View span (past, no longer scheduling)' : 'Edit span'}
                    >
                      {past ? 'View' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      className={styles.spanActionButton}
                      onClick={() => handleSpanDelete(span.id)}
                      disabled={disabled}
                      title="Delete span"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Span Modal */}
      <ScheduleSpanModal
        isOpen={isModalOpen}
        span={editingSpan}
        initialValues={initialModalValues}
        onClose={handleModalClose}
        onSave={handleSpanSave}
      />
    </div>
  );
};
